/**
 * One window for reply, reply all, forward and new email (Liam, 2026-10-15: "all the features that Outlook has"):
 * To/Cc/Bcc you can change, formatted text, signature choice, files (computer, ONE, FusionDocs, or a FusionDocs link),
 * a forward's original files you can leave out, importance, read receipt, remind if no reply, send later and save to
 * Outlook's Drafts. Switching Reply / Reply all / Forward keeps what you wrote.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, CalendarClock, ChevronDown, FileText, Forward, Link2, MailCheck, MessageSquarePlus, Paperclip, Reply, ReplyAll, Save, Send, Sparkles, Undo2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { api, post } from "../../lib/api";
import { RichEditor } from "../RichEditor";
import { MailFiles, useMailFiles } from "../MailAttach";
import { ErrorNote } from "../ui";
import { RecipientInput } from "./RecipientInput";
import { TemplatePicker } from "./pro";

export type ComposeMode = "reply" | "reply_all" | "forward" | "new";
export interface ComposeSource { id: string; fromName: string; fromEmail: string; subject: string; draft?: string | null }
interface Sig { id: string; name: string; html: string; defaultNew: boolean; defaultReply: boolean }

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
const textToHtml = (t: string) => (/^\s*</.test(t) ? t : t.split(/\n{2,}/).map((p) => `<p>${esc(p).replace(/\n/g, "<br>")}</p>`).join(""));
const human = (n: number) => (n > 1e6 ? `${(n / 1e6).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1e3))} KB`);
const at = (d: Date, h: number) => { const x = new Date(d); x.setHours(h, 0, 0, 0); return x; };
function laterChoices() {
  const now = new Date();
  const tomorrow = at(new Date(now.getTime() + 86_400_000), 8);
  const monday = (() => { const x = at(now, 8); x.setDate(x.getDate() + (((8 - x.getDay()) % 7) || 7)); return x; })();
  const later = new Date(now.getTime() + 3 * 3_600_000); later.setMinutes(0, 0, 0);
  return [["In 3 hours", later], ["Tomorrow 08:00", tomorrow], ["Monday 08:00", monday]] as Array<[string, Date]>;
}
const fmtWhen = (d: Date) => d.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });

export interface SharedSource { address: string; gid: string; subject: string; from: string; fromName: string; to: string[]; cc: string[] }

/** The editor's HTML as plain text with line breaks — what "Polish my text" sends to the AI. */
function htmlToText(html: string): string {
  const div = document.createElement("div");
  div.innerHTML = html.replace(/<br\s*\/?>/gi, "\n").replace(/<\/(p|div|li|h[1-6]|blockquote|tr)>/gi, "$&\n");
  return (div.textContent ?? "").replace(/\u00a0/g, " ").replace(/\n{3,}/g, "\n\n").trim();
}

export function MailComposer({ initial, source, shared, canSend, onClose, onQueued, autoDraft }: {
  initial: ComposeMode; source?: ComposeSource | null; canSend: boolean; onClose: () => void;
  /** replying to / forwarding an email in one of your shared mailboxes, as that mailbox */
  shared?: SharedSource | null;
  onQueued?: (q: { outboxId: string; sendAt: string; label: string; scheduled?: boolean }) => void;
  /** open with Fusion AI already writing from this instruction (e.g. "suggest another time") */
  autoDraft?: string;
}) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<ComposeMode>(initial);
  const [to, setTo] = useState<string[]>([]);
  const [cc, setCc] = useState<string[]>([]);
  const [bcc, setBcc] = useState<string[]>([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [names, setNames] = useState<Record<string, string>>(() => (source ? { [source.fromEmail.toLowerCase()]: source.fromName } : {}));
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState(() => (source?.draft ? textToHtml(source.draft) : ""));
  const [editorKey, setEditorKey] = useState(0);
  const [instruction, setInstruction] = useState(autoDraft ?? "");
  const [showNote, setShowNote] = useState(!!autoDraft || initial === "new"); // a new email needs to know what to say
  const [beforeAi, setBeforeAi] = useState<string | null>(null); // your own text, so "Undo AI" can put it back
  const [fromBox, setFromBox] = useState<string>(shared?.address ?? "");
  const [sigId, setSigId] = useState<string | null | undefined>(undefined); // undefined = not chosen yet → the default
  const [importance, setImportance] = useState<"normal" | "high" | "low">("normal");
  const [receipt, setReceipt] = useState(false);
  const [remind, setRemind] = useState<number | null>(null);
  const [laterOpen, setLaterOpen] = useState(false);
  const [laterPick, setLaterPick] = useState("");
  const [picker, setPicker] = useState(false);
  const [dropped, setDropped] = useState<string[]>([]);
  const [busy, setBusy] = useState<"" | "send" | "draft" | "ai">("");
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState<{ webLink: string | null } | null>(null);
  const files = useMailFiles();
  const boxes = useQuery({ queryKey: ["mail-shared"], queryFn: () => api<{ items: Array<{ address: string; name: string | null }> }>("/api/mail/shared"), enabled: mode === "new" && !shared });
  // a shared mailbox's email: start from what's on it (it isn't in ONE's own list)
  useEffect(() => {
    if (!shared) return;
    const me = shared.address.toLowerCase();
    const to = mode === "forward" ? [] : [shared.from, ...(mode === "reply_all" ? shared.to : [])].filter((e) => e && e !== me);
    setTo([...new Set(to)]); setCc(mode === "reply_all" ? shared.cc.filter((e) => e !== me && !to.includes(e)) : []);
    setNames((n) => ({ ...n, [shared.from]: shared.fromName }));
    setSubject(/^(re|fw|fwd):/i.test(shared.subject) ? shared.subject : `${mode === "forward" ? "FW" : "RE"}: ${shared.subject}`);
  }, [shared, mode]);

  const sigs = useQuery({ queryKey: ["mail-signatures"], queryFn: () => api<{ items: Sig[] }>("/api/mail/signatures") });
  const defaultSig = (sigs.data?.items ?? []).find((s) => (mode === "new" ? s.defaultNew : s.defaultReply))?.id ?? null;
  const chosenSig = sigId === undefined ? defaultSig : sigId;
  const sig = (sigs.data?.items ?? []).find((s) => s.id === chosenSig) ?? null;

  // what a reply / reply all / forward starts with: recipients, subject, the original's files
  const start = useQuery({
    queryKey: ["mail-compose", source?.id, mode], enabled: !!source && !shared && mode !== "new",
    queryFn: () => api<{ to: string[]; cc: string[]; subject: string; attachments: Array<{ name: string; size: number }> }>(`/api/mail/messages/${source!.id}/compose?mode=${mode}`),
  });
  useEffect(() => {
    if (!start.data) return;
    setTo(start.data.to); setCc(start.data.cc); setShowCc((v) => v || start.data!.cc.length > 0); setSubject(start.data.subject); setDropped([]);
  }, [start.data]);

  // One AI button: an empty body → "Draft for me"; your own words in the body → "Polish my text" (keeps your points, adds nothing).
  const ai = async () => {
    const own = htmlToText(html);
    if ((mode === "new" || mode === "forward") && !own && !instruction.trim()) {
      setShowNote(true);
      setError(new Error("Write something in the box, or add instructions for the AI"));
      return;
    }
    setBusy("ai"); setError(null);
    try {
      const prev = html;
      if (mode === "new" || mode === "forward") {
        const r = await post<{ subject: string; body: string }>("/api/mail/compose/draft", { to, subject: subject.trim() || undefined, instruction: instruction.trim() || undefined, body: own || undefined });
        setHtml(textToHtml(r.body)); if (mode === "new" && !subject.trim()) setSubject(r.subject);
      } else if (!source) {
        throw new Error("Fusion AI drafting works on your own inbox's emails");
      } else {
        const r = await post<{ draft: string }>(`/api/mail/messages/${source.id}/draft`, { instruction: instruction.trim() || undefined, body: own || undefined });
        setHtml(textToHtml(r.draft));
      }
      setBeforeAi(prev);
      setEditorKey((k) => k + 1);
    } catch (e) { setError(e); }
    setBusy("");
  };
  const undoAi = () => {
    if (beforeAi === null) return;
    setHtml(beforeAi); setBeforeAi(null); setEditorKey((k) => k + 1);
  };

  const payload = (sendAt?: Date) => ({
    mode, messageId: shared ? undefined : source?.id, fromMailbox: shared ? shared.address : fromBox || null, sharedGraphId: shared?.gid, to, cc, bcc, subject: mode === "new" ? subject : undefined, html, signatureId: chosenSig,
    attachments: files.ids, dropAttachments: dropped, importance, readReceipt: receipt, remindDays: remind, ...(sendAt ? { sendAt: sendAt.toISOString() } : {}),
  });
  const send = async (sendAt?: Date) => {
    setBusy("send"); setError(null);
    try {
      const r = await post<{ outboxId: string; sendAt: string; scheduled: boolean }>("/api/mail/send", payload(sendAt));
      void qc.invalidateQueries({ queryKey: ["mail"] });
      onQueued?.({ outboxId: r.outboxId, sendAt: r.sendAt, scheduled: r.scheduled, label: mode === "new" ? `“${subject}” to ${to.join(", ")}` : subject || "your email" });
      onClose();
    } catch (e) { setError(e); setBusy(""); }
  };
  const saveDraft = async () => {
    setBusy("draft"); setError(null); setSaved(null);
    try { setSaved(await post<{ webLink: string | null }>("/api/mail/drafts", payload())); } catch (e) { setError(e); }
    setBusy("");
  };

  useEffect(() => { if (autoDraft && source) void ai(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const empty = !html.replace(/<[^>]+>/g, "").trim() && !/<img/i.test(html);
  const words = empty ? 0 : htmlToText(html).split(/\s+/).filter(Boolean).length;
  const aiLabel = busy === "ai" ? (empty ? "Writing…" : "Polishing…") : empty ? (mode === "new" ? "Write for me" : mode === "forward" ? "Write a note" : "Draft for me") : "Polish my text";
  const blocked = !to.length || empty || (mode === "new" && !subject.trim()) || files.busy || files.broken || !!busy;
  const why = !to.length ? "Add a recipient" : empty ? "Write something first" : mode === "new" && !subject.trim() ? "Add a subject" : files.busy ? "Wait for the files to upload" : files.broken ? "Remove the file that failed" : undefined;
  const modes: Array<[ComposeMode, string, React.ReactNode]> = [["reply", "Reply", <Reply size={13} key="r" />], ["reply_all", "Reply all", <ReplyAll size={13} key="a" />], ["forward", "Forward", <Forward size={13} key="f" />]];

  return (
    <div className="mt-3 space-y-2 rounded-xl border border-[var(--line)] bg-[var(--panel-2)] p-3" {...files.dropProps}>
      {(source || shared) && (
        <div className="flex flex-wrap items-center gap-2">
          {shared && <span className="chip !py-0.5 text-xs">As {shared.address}</span>}
          <div className="inline-flex overflow-hidden rounded-lg border border-[var(--line)]" role="group" aria-label="Reply or forward">
            {modes.map(([m, label, icon]) => (
              <button key={m} type="button" aria-pressed={mode === m} onClick={() => setMode(m)} className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs ${mode === m ? "bg-brand-50 font-semibold text-brand-700" : "hover:bg-black/[0.04]"}`}>{icon}{label}</button>
            ))}
          </div>
          <span className="muted min-w-0 flex-1 truncate text-xs">{subject}</span>
        </div>
      )}

      <div className="rounded-lg bg-[var(--panel)] px-2">
        {mode === "new" && !!boxes.data?.items.length && (
          <div className="flex items-center gap-1 border-b border-[var(--line)] py-1 text-sm"><span className="muted w-9 shrink-0 text-xs">From</span>
            <select className="flex-1 bg-transparent px-1 py-1 text-sm outline-none" value={fromBox} onChange={(e) => setFromBox(e.target.value)} aria-label="From">
              <option value="">Me</option>
              {boxes.data.items.map((b) => <option key={b.address} value={b.address}>{b.name ? `${b.name} <${b.address}>` : b.address}</option>)}
            </select>
          </div>
        )}
        <RecipientInput label="To" value={to} onChange={setTo} names={names} onName={(e, n) => setNames((x) => ({ ...x, [e]: n }))} autoFocus={mode === "forward" || mode === "new"}
          extra={<span className="flex gap-2 pr-1 text-xs">{!showCc && <button type="button" className="text-brand-700 hover:underline" onClick={() => setShowCc(true)}>Cc</button>}{!showBcc && <button type="button" className="text-brand-700 hover:underline" onClick={() => setShowBcc(true)}>Bcc</button>}</span>} />
        {showCc && <RecipientInput label="Cc" value={cc} onChange={setCc} names={names} onName={(e, n) => setNames((x) => ({ ...x, [e]: n }))} />}
        {showBcc && <RecipientInput label="Bcc" value={bcc} onChange={setBcc} names={names} onName={(e, n) => setNames((x) => ({ ...x, [e]: n }))} />}
        {mode === "new" && (
          <div className="flex items-center gap-1 py-1 text-sm"><span className="muted w-9 shrink-0 text-xs">Subj</span>
            <input className="flex-1 bg-transparent px-1 py-1 outline-none" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={400} placeholder="Subject (empty: the AI suggests one)" aria-label="Subject" />
          </div>
        )}
      </div>

      <RichEditor key={editorKey} value={html} onChange={setHtml} minHeight={260}
        placeholder={mode === "forward" ? "Add a note — the email and its attachments go underneath" : shared ? "Write your email…" : mode === "new" ? "Write your email in your own words — short notes are fine — then press Polish my text. Or leave it empty, add instructions for the AI and press Write for me." : "Write your reply in your own words — short notes are fine — then press Polish my text. Or leave it empty and press Draft for me."} />

      <div className="flex flex-wrap items-center gap-2">
        {!shared && <>
          <button type="button" className="btn !min-h-0 !px-2 !py-1 text-xs" disabled={!!busy} onClick={() => void ai()} title={empty ? "Fusion AI writes it for you" : "Fusion AI turns your own words into a proper email — it keeps your points and adds nothing new"}><Sparkles size={13} /> {aiLabel}</button>
          {beforeAi !== null && busy !== "ai" && <button type="button" className="btn !min-h-0 !px-2 !py-1 text-xs" onClick={undoAi} title="Put back what you had before the AI"><Undo2 size={13} /> Undo AI</button>}
          <button type="button" className="btn !min-h-0 !border-transparent !px-2 !py-1 text-xs" aria-expanded={showNote} onClick={() => setShowNote((v) => !v)}><MessageSquarePlus size={13} /> Instructions for AI</button>
        </>}
        <TemplatePicker onPick={(t) => { setHtml((h) => `${h}${t}`); setEditorKey((k) => k + 1); }} />
        <span className="muted ml-auto text-xs">{words} word{words === 1 ? "" : "s"}</span>
      </div>
      {showNote && !shared && (
        <textarea className="input min-h-[4.5rem] resize-y font-[inherit] text-xs" rows={3} maxLength={4000} value={instruction} onChange={(e) => setInstruction(e.target.value)}
          placeholder={mode === "new" ? "What the email should say, or how to write it — as long as you like, e.g. ask Mark for the Q3 occupancy figures by Friday, keep it friendly" : "Optional: tone or extra points, as long as you like, e.g. keep it short, mention the deposit was paid on 3 Oct, offer Thursday or Friday"} />
      )}

      <div className="flex flex-wrap items-start gap-2 text-xs">
        <label className="muted flex items-center gap-1">Signature
          <select className="input !w-auto !py-0.5 text-xs" value={chosenSig ?? ""} onChange={(e) => setSigId(e.target.value || null)} aria-label="Signature">
            <option value="">None</option>
            {(sigs.data?.items ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        {sig?.html && <div className="muted min-w-0 flex-1 border-l-2 border-[var(--line)] pl-2 [&_img]:max-h-10" dangerouslySetInnerHTML={{ __html: sig.html }} />}
        {!(sigs.data?.items ?? []).length && <span className="muted">No signature yet — add one in Mail settings.</span>}
      </div>

      {(start.data?.attachments.length ?? 0) > 0 && mode === "forward" && (
        <div className="flex flex-wrap items-center gap-1.5 text-xs">
          <span className="muted">From the email:</span>
          {start.data!.attachments.map((a) => {
            const off = dropped.includes(a.name);
            return (
              <span key={a.name} className={`chip gap-1 !py-0.5 ${off ? "line-through opacity-50" : ""}`} title={off ? "Left out — click to add back" : "Goes with the forward"}>
                <Paperclip size={11} />{a.name} <span className="muted">{human(a.size)}</span>
                <button type="button" onClick={() => setDropped((d) => (off ? d.filter((x) => x !== a.name) : [...d, a.name]))} aria-label={off ? `Add back ${a.name}` : `Leave out ${a.name}`}>{off ? "+" : <X size={11} />}</button>
              </span>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <MailFiles state={files} compact />
        <button type="button" className="btn !min-h-0 !py-1.5 text-xs" onClick={() => setPicker((v) => !v)}><FileText size={13} /> From ONE / FusionDocs</button>
      </div>
      {picker && <AttachPicker onAttach={(d) => files.addDoc(d)} onLink={(title, url) => { setHtml((h) => `${h}<p><a href="${esc(url)}">${esc(title)}</a></p>`); setEditorKey((k) => k + 1); }} onClose={() => setPicker(false)} />}

      <div className="flex flex-wrap items-center gap-2 text-xs">
        <button type="button" className={`btn !min-h-0 !py-1 text-xs ${importance === "high" ? "!border-red-300 !bg-red-50 text-red-700" : ""}`} aria-pressed={importance === "high"} onClick={() => setImportance((v) => (v === "high" ? "normal" : "high"))}><AlertCircle size={13} /> High importance</button>
        <button type="button" className={`btn !min-h-0 !py-1 text-xs ${receipt ? "!border-brand-300 !bg-brand-50 text-brand-700" : ""}`} aria-pressed={receipt} onClick={() => setReceipt((v) => !v)}><MailCheck size={13} /> Read receipt</button>
        <select className="input !w-auto !py-1 text-xs" value={remind ?? ""} onChange={(e) => setRemind(e.target.value ? Number(e.target.value) : null)} aria-label="Remind me if no reply">
          <option value="">No reminder</option>
          {[2, 3, 5, 7].map((d) => <option key={d} value={d}>Remind if no reply in {d} days</option>)}
        </select>
      </div>

      <ErrorNote error={error} />
      {saved && <p className="text-xs text-emerald-700">Saved in your Outlook Drafts{saved.webLink ? <> · <a className="underline" href={saved.webLink} target="_blank" rel="noopener noreferrer">Open in Outlook</a></> : null}.</p>}

      <div className="flex flex-wrap items-center gap-2">
        {canSend ? (
          <>
            <span className="inline-flex">
              <button type="button" className="btn btn-primary !min-h-0 !rounded-r-none !py-1.5 text-xs" disabled={blocked} title={why} onClick={() => void send()}><Send size={13} /> {busy === "send" ? "Sending…" : "Send"}</button>
              <button type="button" className="btn btn-primary !min-h-0 !rounded-l-none border-l border-white/30 !px-1.5 !py-1.5 text-xs" disabled={blocked} aria-label="Send later" title="Send later" onClick={() => setLaterOpen((v) => !v)}><ChevronDown size={13} /></button>
            </span>
            {laterOpen && (
              <span className="flex flex-wrap items-center gap-1.5 rounded-lg bg-[var(--panel)] px-2 py-1 text-xs ring-1 ring-[var(--line)]">
                <CalendarClock size={13} className="muted" /> Send later:
                {laterChoices().map(([l, d]) => <button key={l} type="button" className="chip cursor-pointer !py-0.5" disabled={blocked} title={fmtWhen(d)} onClick={() => void send(d)}>{l}</button>)}
                <input type="datetime-local" className="input !w-auto !py-0.5 text-xs" value={laterPick} onChange={(e) => setLaterPick(e.target.value)} aria-label="Pick a time" />
                <button type="button" className="btn btn-sm !py-0.5" disabled={blocked || !laterPick} onClick={() => void send(new Date(laterPick))}>Schedule</button>
              </span>
            )}
            {!shared && !fromBox && <button type="button" className="btn !min-h-0 !py-1.5 text-xs" disabled={!!busy} onClick={() => void saveDraft()}><Save size={13} /> {busy === "draft" ? "Saving…" : "Save draft"}</button>}
          </>
        ) : (
          <a className="btn btn-primary !min-h-0 !py-1.5 text-xs" href="/api/personal/microsoft/connect?send=1">Allow sending from ONE</a>
        )}
        <button type="button" className="btn !min-h-0 !py-1.5 text-xs" onClick={onClose}>Close</button>
        <span className="muted text-xs">Sending waits 15 seconds so you can undo.</span>
      </div>
    </div>
  );
}

/** Attach a file you already have in ONE, or one from FusionDocs (as a copy, or as a link for people with access). */
function AttachPicker({ onAttach, onLink, onClose }: { onAttach: (d: { id: string; name: string; size: number }) => void; onLink: (title: string, url: string) => void; onClose: () => void }) {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState<unknown>(null);
  const r = useQuery({
    queryKey: ["mail-attachables", q.trim()],
    queryFn: () => api<{ one: Array<{ id: string; title: string; filename: string; size: number }>; fusionDocs: Array<{ id: string; title: string; url: string | null; path: string | null }> }>(`/api/mail/attachables${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`),
  });
  const copy = async (id: string) => {
    setBusy(id); setErr(null);
    try { const d = await post<{ id: string; name: string; size: number }>("/api/mail/attachables/fusiondocs", { id }); onAttach(d); } catch (e) { setErr(e); }
    setBusy("");
  };
  return (
    <div className="space-y-2 rounded-lg bg-[var(--panel)] p-2 text-xs ring-1 ring-[var(--line)]">
      <div className="flex items-center gap-2">
        <input className="input flex-1 !py-1 text-xs" autoFocus placeholder="Search your ONE files and FusionDocs…" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="button" className="btn btn-sm" onClick={onClose} aria-label="Close"><X size={13} /></button>
      </div>
      <ErrorNote error={err} />
      <div className="grid gap-2 sm:grid-cols-2">
        <div>
          <div className="section-label mb-1">Your ONE files</div>
          <ul className="max-h-44 overflow-auto">
            {(r.data?.one ?? []).map((d) => (
              <li key={d.id}><button type="button" className="w-full truncate rounded px-1.5 py-1 text-left hover:bg-black/[0.04]" onClick={() => onAttach({ id: d.id, name: d.filename, size: d.size })}><Paperclip size={11} className="mr-1 inline" />{d.filename} <span className="muted">{human(d.size)}</span></button></li>
            ))}
            {r.data && !r.data.one.length && <li className="muted px-1.5 py-1">Nothing here.</li>}
          </ul>
        </div>
        <div>
          <div className="section-label mb-1">FusionDocs</div>
          {!q.trim() ? <p className="muted px-1.5">Type to search FusionDocs.</p> : (
            <ul className="max-h-44 overflow-auto">
              {(r.data?.fusionDocs ?? []).map((d) => (
                <li key={d.id} className="flex items-center gap-1 rounded px-1.5 py-1 hover:bg-black/[0.04]">
                  <span className="min-w-0 flex-1 truncate" title={d.path ?? undefined}>{d.title}</span>
                  <button type="button" className="text-brand-700 hover:underline" disabled={!!busy} onClick={() => void copy(d.id)}>{busy === d.id ? "…" : "Attach"}</button>
                  {d.url && <button type="button" className="text-brand-700 hover:underline" onClick={() => onLink(d.title, d.url!)} title="Insert a link (for people with access in FusionDocs)"><Link2 size={11} className="inline" /> Link</button>}
                </li>
              ))}
              {r.data && !r.data.fusionDocs.length && <li className="muted px-1.5 py-1">No match in FusionDocs.</li>}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
